<!--
    CameraDot.svelte

    One participant, reduced to an avatar, for the docked state of CamerasDock.

    Reuses ParticipantWoka - the same avatar circle the action bar already shows - so a person
    looks the same wherever they appear. The only thing added here is the ring, lit while that
    person is speaking.
-->
<script lang="ts">
    import { writable } from "svelte/store";
    import type { VideoBox } from "../../Space/VideoBox";
    import ParticipantWoka from "../ActionBar/MenuIcons/ParticipantWoka.svelte";

    interface Props {
        videoBox: VideoBox;
    }

    let { videoBox }: Props = $props();

    let streamableStore = $derived(videoBox.streamable);
    let streamable = $derived($streamableStore);

    let spaceUser = $derived(videoBox.spaceUser);
    let pictureStore = $derived(spaceUser?.pictureStore ?? writable(""));
    let name = $derived(spaceUser?.name ?? "");

    // Same store the bubble uses for its "is talking" background, so the dot and the bubble
    // can never disagree about who has the floor.
    let showVoiceIndicatorStore = $derived(streamable?.showVoiceIndicator);
    let isSpeaking = $derived(showVoiceIndicatorStore ? $showVoiceIndicatorStore : false);
</script>

<div class="group/dot relative flex items-center" data-testid="camera-dot">
    <div
        class="rounded-full transition-all duration-150"
        class:speaking={isSpeaking}
        class:quiet={!isSpeaking}
        aria-label={name}
    >
        <ParticipantWoka {pictureStore} fallbackName={name} />
    </div>

    <!-- The dot is enough to see who is talking, not enough to see who is who. -->
    <div
        class="pointer-events-none absolute start-11 whitespace-nowrap rounded bg-contrast/90 px-2 py-1 text-xs text-white opacity-0 backdrop-blur transition-opacity duration-150 group-hover/dot:opacity-100"
    >
        {name}
    </div>
</div>

<style>
    .quiet {
        opacity: 0.55;
        box-shadow: 0 0 0 2px rgb(255 255 255 / 15%);
    }

    .speaking {
        opacity: 1;
        transform: scale(1.12);
        /* #4156f6 is the design system's `secondary` - the same blue the bubble paints behind
           the name when someone talks (DesignSystemPhaserColors.ts). Same cue, dot-sized. */
        box-shadow: 0 0 0 2px rgb(65 86 246), 0 0 0 6px rgb(65 86 246 / 35%);
    }
</style>
